import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { BranchingQueryDefinition, DefaultScoreEquationCoefficients, getEntityToScoreConfigEdgeTypeId, getScoreFromNormalForReading, ScoreConfig, ScoreConfigClassName, } from '@iotv/datamodel-core';
import { isNonNaNNumber, objectsMatch, PathDirection, ReadingKeyToScoreKey } from '@iotv/iotv-v3-types';
import { Alert, Autocomplete, Button, ButtonGroup, Skeleton, Stack, Typography, useTheme } from '@mui/material';
import { ChartsReferenceLine, ChartsXAxis, ChartsYAxis, LineChart, LinePlot } from '@mui/x-charts';
import { useEffect, useMemo, useState } from 'react';
import { v4 } from 'uuid';
import Assembler from '../../data/Assembler';
import { ConsumerDao } from '../../data/ConsumerDAO';
import { isAdminRole } from '../../helpers/access';
import { ActionIcons } from '../../Iconography';
import { AdjacentType, Severity } from '../../types';
import { AutosizedPopper } from '../common/AutosizedPopper';
import { AutosizedTextField } from '../common/AutosizedTextField';
import { NumericField } from '../common/NumericField';
import { ScoreEquation } from './ScoreEquation';
const debug = process.env.REACT_APP_DEBUG && false;
const defaultA = DefaultScoreEquationCoefficients.a;
const defaultB = DefaultScoreEquationCoefficients.b;
const defaultN = DefaultScoreEquationCoefficients.n;
const minSteps = 10;
const maxSteps = 500;
const formatter = new Intl.NumberFormat(undefined, { notation: 'compact', maximumFractionDigits: 1 });
/**
 * CRA: All the `useEffect()`'s are very delicately balanced, beware!
 *
 * Currently, this has to balance:
 * - loading a {@link ScoreConfig} from the datastore
 * - user input editing a {@link ScoreConfig}
 *
 * These two sources of truth both contribute to the state of the local {@link ScoreConfig}, and must accomodate initialisation and reset and all states in between
 *
 * *Before touching anything, take great care:*
 *
 * ```ts
 * const extra_hours_spent_fighting_React = 6
 * const extra_hours_spent_fighting_LSC = 1
 * ```
 */
export const EntityScoresConfig = ({ entity, objectHistory, userSelectedGroup, isSmallView, userFunctions }) => {
    const theme = useTheme();
    const hasStats = 0 < Object.keys(objectHistory.recentStateHistory?.getStatistics() ?? {}).length;
    debug && console.log(`ObjectHistoryStatistics history store has stats?`, hasStats);
    const statistics = hasStats ? objectHistory.recentStateHistory?.getStatistics() : objectHistory.recentStateHistory?.generateStatistics();
    const isAdmin = isAdminRole(userSelectedGroup);
    const userAttributes = useMemo(() => Assembler.getInstance(entity)?.getUserAttributes(), [entity.sk]);
    const [saving, setSaving] = useState(false);
    const [removing, setRemoving] = useState(false);
    const [storedScoreConfig, setStoredScoreConfig] = useState();
    const [scoreConfig, setScoreConfig] = useState(new ScoreConfig({}));
    useEffect(() => {
        ConsumerDao.getGraphQueryRequestV2({
            contextObjects: [entity],
            query: new BranchingQueryDefinition({ id: entity.sk, path: [{ objectTypeId: ScoreConfigClassName, adjacencyType: AdjacentType.CHILD }] }),
        }).then(({ err, data }) => {
            if (err || !data)
                return console.log('EntityScoresConfig', err);
            const loadedConfig = data.getNodesOfType(ScoreConfigClassName).at(0);
            if (!loadedConfig)
                return;
            setStoredScoreConfig(new ScoreConfig(structuredClone(loadedConfig)));
            setScoreConfig(new ScoreConfig(structuredClone(loadedConfig)));
        });
    }, [entity.sk]);
    const edited = !objectsMatch(storedScoreConfig?.definitions ?? {}, scoreConfig.definitions) || !objectsMatch(scoreConfig.definitions, storedScoreConfig?.definitions ?? {});
    const canSave = edited || storedScoreConfig === undefined;
    const [selectedKey, setSelectedKey] = useState(null);
    // normals
    const normalCMVar = selectedKey ? (statistics?.[selectedKey] ?? undefined) : undefined;
    const normalMin = normalCMVar?.min !== undefined ? Math.round(normalCMVar.min) : undefined;
    const normalMean = selectedKey ? Math.round(statistics?.[selectedKey]?.mean ?? 0) : undefined;
    const normalMax = normalCMVar?.max !== undefined ? Math.round(normalCMVar.max) : undefined;
    const [min, setMin] = useState(0);
    const [normal, setNormal] = useState(0);
    const [max, setMax] = useState(0);
    useEffect(() => {
        if (normalCMVar) {
            setMin(normalMin !== undefined ? Math.round(normalMin) : 0);
            setNormal(normalMean !== undefined ? Math.round(normalMean) : 0);
            setMax(normalMax !== undefined ? Math.round(normalMax) : 0);
        }
        else {
            setMin(0);
            setNormal(0);
            setMax(0);
        }
    }, [normalMin, normalMean, normalMax]);
    // coefficients
    const selectedDefinition = selectedKey ? scoreConfig?.definitions[selectedKey] : undefined;
    const selectedAboveA = selectedDefinition?.above.a;
    const selectedAboveB = selectedDefinition?.above.b;
    const selectedAboveN = selectedDefinition?.above.n;
    const selectedBelowA = selectedDefinition?.below.a;
    const selectedBelowB = selectedDefinition?.below.b;
    const selectedBelowN = selectedDefinition?.below.n;
    const [aboveA, setAboveA] = useState(defaultA);
    const [aboveB, setAboveB] = useState(defaultB);
    const [aboveN, setAboveN] = useState(defaultN);
    const [belowA, setBelowA] = useState(defaultA);
    const [belowB, setBelowB] = useState(defaultB);
    const [belowN, setBelowN] = useState(defaultN);
    const coefficientsAbove = { a: aboveA, b: aboveB, n: aboveN };
    const coefficientsBelow = { a: belowA, b: belowB, n: belowN };
    // when switching to a new key, initialise (so that the default values can be immediately saved)
    useEffect(() => {
        if (selectedKey === null)
            return;
        if (scoreConfig.definitions[selectedKey] !== undefined)
            return;
        setScoreConfig((c) => {
            const update = new ScoreConfig(structuredClone(c));
            update.definitions[selectedKey] = {
                key: selectedKey,
                above: structuredClone(DefaultScoreEquationCoefficients),
                below: structuredClone(DefaultScoreEquationCoefficients),
            };
            return update;
        });
    }, [selectedKey]);
    // when the definition changes (e.g. updating the local state from a datastore load, or switching key), update the coefficient state accordingly
    useEffect(() => {
        debug && console.log('EntityScoresConfig', 'updating definition', selectedDefinition?.below.a ?? defaultA);
        setAboveA(selectedAboveA ?? defaultA);
        setAboveB(selectedAboveB ?? defaultB);
        setAboveN(selectedAboveN ?? defaultN);
        setBelowA(selectedBelowA ?? defaultA);
        setBelowB(selectedBelowB ?? defaultB);
        setBelowN(selectedBelowN ?? defaultN);
    }, [selectedDefinition]);
    useEffect(() => {
        if (selectedKey === null)
            return;
        const aboveMatches = objectsMatch(coefficientsAbove, selectedDefinition?.above ?? {});
        const belowMatches = objectsMatch(coefficientsBelow, selectedDefinition?.below ?? {});
        const matches = aboveMatches && belowMatches;
        debug && console.log('EntityScoresConfig', 'ScoreConfig update', matches, selectedDefinition?.below.a, coefficientsBelow.a);
        if (matches)
            return;
        debug && console.log('EntityScoresConfig', 'should update ScoreConfig', selectedDefinition?.below.a ?? defaultA, coefficientsBelow.a);
        setScoreConfig((c) => {
            const update = new ScoreConfig(structuredClone(c));
            update.definitions[selectedKey] = {
                key: selectedKey,
                above: structuredClone(coefficientsAbove),
                below: structuredClone(coefficientsBelow),
            };
            return update;
        });
    }, [aboveA, aboveB, aboveN, belowA, belowA, belowB, belowN]);
    debug &&
        console.log('EntityScoresConfig', edited, storedScoreConfig?.definitions[selectedKey ?? '']?.below.a, scoreConfig.definitions[selectedKey ?? '']?.below.a, belowA);
    const precision = selectedKey && selectedKey in userAttributes ? userAttributes[selectedKey]?.precision : undefined;
    const inferredRange = Math.abs(max - min);
    const inferredSteps = Math.max(minSteps, Math.min(inferredRange, maxSteps));
    const inferredStepSize = inferredRange / inferredSteps;
    const preferredStepSize = precision !== undefined ? Math.pow(10, -precision) : inferredStepSize;
    const preferredSteps = inferredRange / preferredStepSize;
    const stepSize = minSteps <= preferredSteps && preferredSteps <= maxSteps ? preferredStepSize : inferredStepSize;
    const dataset = [];
    if (0 < inferredRange && 0 < stepSize)
        for (let i = min; i <= max; i += stepSize)
            dataset.push({ x: i, y: getScoreFromNormalForReading({ scoreDefinition: selectedDefinition, reading: i, normal }) });
    if (objectHistory.recentStateHistory === undefined)
        return _jsx(Skeleton, { variant: "rounded", width: "100cqw", height: "3em" });
    const availableKeys = (objectHistory.recentStateHistory.keys() ?? []).sort((a, b) => a.localeCompare(b));
    const validKeys = availableKeys.filter((k) => availableKeys.includes(ReadingKeyToScoreKey(k)));
    const buttons = (_jsxs(ButtonGroup, { children: [_jsx(Button, { startIcon: _jsx(ActionIcons.Reset, {}), variant: "contained", disabled: !edited || saving || removing, onClick: () => setScoreConfig(new ScoreConfig(structuredClone(storedScoreConfig ?? {}))), children: "Reset" }, "reset"), _jsx(Button, { startIcon: _jsx(ActionIcons.Save, {}), variant: "contained", disabled: !canSave || saving || removing, loading: saving, loadingPosition: "start", onClick: () => {
                    setSaving(true);
                    ConsumerDao.createAndLink({
                        existingObject: entity,
                        newObject: scoreConfig,
                        direction: PathDirection.child,
                        edgeTypeId: getEntityToScoreConfigEdgeTypeId(entity.type),
                    }).then(({ err, data }) => {
                        setSaving(false);
                        if (err || !data) {
                            userFunctions.setUserMessage({ id: v4(), type: 'SnackBar', severity: Severity.error, label: 'Error', content: 'Error saving Score Config!' });
                            return;
                        }
                        const [source, edge, target] = data;
                        if (target) {
                            setStoredScoreConfig(new ScoreConfig(structuredClone(target)));
                            userFunctions.setUserMessage({ id: v4(), type: 'SnackBar', severity: Severity.success, label: 'Saved', content: 'Saved Score Config!' });
                        }
                    });
                }, children: "Save" }, "save"), isAdmin && (_jsx(Button, { variant: "contained", color: "error", disabled: storedScoreConfig === undefined || scoreConfig === undefined || saving || removing, loading: removing, loadingPosition: "start", onClick: () => {
                    if (storedScoreConfig === undefined)
                        return;
                    setRemoving(true);
                    ConsumerDao.deleteObject(storedScoreConfig).then(({ err, data }) => {
                        setRemoving(false);
                        if (err || !data) {
                            userFunctions.setUserMessage({ id: v4(), type: 'SnackBar', severity: Severity.error, label: 'Error', content: 'Error removing Score Config!' });
                            return;
                        }
                        setStoredScoreConfig(undefined);
                        setScoreConfig(new ScoreConfig({}));
                        userFunctions.setUserMessage({ id: v4(), type: 'SnackBar', severity: Severity.success, label: 'Saved', content: 'Removed Score Config!' });
                    });
                }, startIcon: _jsx(ActionIcons.Delete, {}), children: "Remove" }, "remove"))] }));
    const alert = selectedKey !== null && storedScoreConfig === undefined && (_jsx(Alert, { severity: "warning", sx: { paddingBlock: 0 }, children: "No score config stored!" }));
    const viewConstraints = (_jsxs(Stack, { direction: "row", alignSelf: "flex-end", gap: 1, sx: { padding: 1, borderRadius: 1.5, backgroundColor: theme.alpha(theme.palette.primary.light, 0.5) }, children: [_jsx(NumericField, { label: "Min", value: normalMin, onChange: (n) => setMin(n ?? 0), fullWidth: isSmallView }, "min"), _jsx(NumericField, { label: "Normal", value: normalMean, onChange: (n) => setNormal(n ?? Math.max(min, Math.min(0, max))), fullWidth: isSmallView }, "normal"), _jsx(NumericField, { label: "Max", value: normalMax, onChange: (n) => setMax(n ?? 0), fullWidth: isSmallView }, "max")] }));
    const belowNormal = (_jsxs(Stack, { direction: isSmallView ? 'column' : 'row', alignItems: "center", gap: 1, sx: { position: 'relative', padding: 1, borderRadius: 1.5, backgroundColor: theme.lighten(theme.palette.primary.light, 0.5) }, children: [_jsx(Typography, { variant: "body2", sx: {
                    position: 'absolute',
                    top: '-0.75em',
                    left: '0.6em',
                    paddingBlock: 0.25,
                    paddingInline: 1,
                    borderRadius: 1.5,
                    backgroundColor: theme.lighten(theme.palette.primary.light, 0.5),
                }, children: "Below normal" }), _jsx(ScoreEquation, { coefficients: coefficientsBelow }), _jsxs(Stack, { direction: "row", alignItems: "center", gap: 1, children: [_jsx(NumericField, { label: "a", value: selectedBelowA ?? defaultA, onChange: (n) => setBelowA(n ?? selectedBelowA ?? defaultA) }, "a"), _jsx(NumericField, { label: "b", value: selectedBelowB ?? defaultB, onChange: (n) => setBelowB(n ?? selectedBelowB ?? defaultB) }, "b"), _jsx(NumericField, { label: "n", value: selectedBelowN ?? defaultN, onChange: (n) => setBelowN(n ?? selectedBelowN ?? defaultN) }, "n")] }, "below")] }));
    const aboveNormal = (_jsxs(Stack, { direction: isSmallView ? 'column' : 'row', alignItems: "center", gap: 1, sx: { position: 'relative', padding: 1, borderRadius: 1.5, backgroundColor: theme.alpha(theme.palette.primary.light, 0.5) }, children: [_jsx(Typography, { variant: "body2", sx: {
                    position: 'absolute',
                    top: '-0.75em',
                    left: '0.6em',
                    paddingBlock: 0.25,
                    paddingInline: 1,
                    borderRadius: 1.5,
                    backgroundColor: theme.lighten(theme.palette.primary.light, 0.5),
                }, children: "Above normal" }), _jsx(ScoreEquation, { coefficients: coefficientsAbove }), _jsxs(Stack, { direction: "row", alignItems: "center", gap: 1, children: [_jsx(NumericField, { label: "a", value: selectedAboveA ?? defaultA, onChange: (n) => setAboveA(n ?? selectedAboveA ?? defaultA) }, "a"), _jsx(NumericField, { label: "b", value: selectedAboveB ?? defaultB, onChange: (n) => setAboveB(n ?? selectedAboveB ?? defaultB) }, "b"), _jsx(NumericField, { label: "n", value: selectedAboveN ?? defaultN, onChange: (n) => setAboveN(n ?? selectedAboveN ?? defaultN) }, "n")] }, "above")] }));
    const inputs = (_jsxs(_Fragment, { children: [buttons, alert, _jsx(Stack, { flexGrow: 1, direction: "row-reverse", justifyContent: isSmallView ? 'center' : undefined, children: viewConstraints })] }));
    return (_jsxs(Stack, { height: isSmallView ? '160cqh' : '80cqh', gap: 1, children: [_jsxs(Stack, { direction: "row", flexWrap: "wrap", alignItems: "center", gap: 1, children: [_jsx(Autocomplete, { value: selectedKey, onChange: (_, v) => setSelectedKey(v), options: validKeys, getOptionLabel: (o) => (typeof userAttributes[o]?.label === 'function' ? userAttributes[o]?.label() : (userAttributes[o]?.label ?? o)), size: "small", fullWidth: isSmallView, slots: { popper: AutosizedPopper }, renderInput: (params) => _jsx(AutosizedTextField, { ...params, label: "Score", error: selectedKey === null }) }), selectedKey !== null && !isSmallView && inputs] }), selectedKey !== null && isSmallView && inputs, selectedKey ? (_jsxs(LineChart, { dataset: dataset, xAxis: [
                    {
                        dataKey: 'x',
                        // @ts-expect-error string|undefined is not assignable to string
                        valueFormatter: (value, context) => context.location === 'tooltip' ? value.toLocaleString(undefined, { maximumFractionDigits: precision }) : undefined,
                    },
                ], yAxis: [{ width: 'auto', valueFormatter: (value) => formatter.format(value) }], series: [
                    {
                        type: 'line',
                        dataKey: 'y',
                        showMark: false,
                        label: 'Score',
                        color: theme.palette.primary.main,
                        valueFormatter: (value) => (isNonNaNNumber(value) ? value.toLocaleString(undefined, { maximumFractionDigits: precision }) : null),
                    },
                ], hideLegend: true, children: [_jsx(LinePlot, {}), 0 < dataset.length && _jsx(ChartsReferenceLine, { label: `Normal: ${normal}`, labelAlign: "start", x: normal, lineStyle: { strokeDasharray: '10 5' } }), _jsx(ChartsXAxis, {}), _jsx(ChartsYAxis, {})] })) : (_jsx(Stack, { flexGrow: 1, justifyContent: "center", alignItems: "center", children: _jsx(Alert, { severity: "info", children: "Please select a score first!" }) })), selectedKey !== null && (_jsxs(Stack, { direction: isSmallView ? 'column' : 'row', justifyContent: "space-evenly", gap: isSmallView ? 2 : 1, children: [belowNormal, aboveNormal] }))] }));
};
